import React, { useState } from "react";
import { createPortal } from "react-dom";
import { Filter, X } from "lucide-react";
import { Input } from "./FormControls";
import { Checkbox } from "./Choice";
import AppButton from "./AppButton";
import useAnchoredLayer from "./useAnchoredLayer";
import cn from "./cn";
import { isColumnFilterActive } from "./tableData";

/**
 * ColumnFilter — the funnel in a table header cell.
 *
 * The ag-Grid arrangement: a column's header carries its own sort control AND
 * its own filter, so narrowing a table is done where the data is rather than in
 * a strip that has to name each column again. `AppDataTable` renders one of
 * these for every column declaring `filter`; nothing else should mount it
 * directly.
 *
 * ⚠️ **The filter is a VALUE, not a predicate.** This component reads and
 * writes `value` and knows nothing about rows — `applyColumnFilters` in
 * `AppDataTable` does the matching, and a server-side table can send the same
 * value upstream instead. Putting the predicate here would make the two
 * impossible to keep in step.
 *
 * ── The four types ──────────────────────────────────────────────────────────
 *   text    value: "abc"                     — case-insensitive contains
 *   set     value: ["live", "draft"]         — a checkbox per option, OR within
 *   number  value: { min: "5", max: "" }     — either end may be blank
 *   date    value: { from: "…", to: "…" }    — ISO yyyy-mm-dd, either may be blank
 *
 * An inactive filter is `undefined`, never an empty string or an empty array:
 * `isColumnFilterActive` is what decides whether the funnel reads as on, and
 * "on, matching everything" is a state that only ever confuses.
 *
 * ⚠️ The panel does NOT close when you change a value — a set filter is
 * normally several clicks, and a panel that vanished after the first would make
 * the second a re-open. Escape, a click outside, and Done all close it.
 */

const PANEL_WIDTH = 232;

/* Defined in `tableData.js` — the pure half, so the predicate can be tested
   without a component tree — and re-exported here because callers have
   imported it from this file since it was written. */
export { isColumnFilterActive };

/** Blank back to `undefined` so an emptied field is indistinguishable from one
 *  that was never touched. */
const orUndefined = (value) => (isColumnFilterActive(value) ? value : undefined);

const FieldLabel = ({ children }) => (
  <span className="tw-text-[0.6875rem] tw-font-semibold tw-uppercase tw-tracking-[0.04em] tw-text-content-subtle">
    {children}
  </span>
);

const TextFilter = ({ value, onChange, placeholder }) => (
  <Input
    size="sm"
    autoFocus
    value={value || ""}
    placeholder={placeholder || "Contains…"}
    onChange={(e) => onChange(orUndefined(e.target.value))}
  />
);

const SetFilter = ({ value, onChange, options }) => {
  const selected = Array.isArray(value) ? value : [];
  const toggle = (v) =>
    onChange(
      orUndefined(selected.includes(v) ? selected.filter((s) => s !== v) : [...selected, v])
    );

  if (options.length === 0) {
    return <span className="tw-text-xs tw-text-content-subtle">Nothing to filter by yet.</span>;
  }

  return (
    // Capped and scrollable: a set filter over, say, every course is otherwise a
    // panel taller than the window, which the flip-up placement cannot rescue.
    <div className="tw-flex tw-max-h-56 tw-flex-col tw-gap-1.5 tw-overflow-y-auto">
      {options.map((o) => (
        <Checkbox
          key={o.value}
          size="sm"
          label={o.label}
          checked={selected.includes(o.value)}
          onChange={() => toggle(o.value)}
        />
      ))}
    </div>
  );
};

/** Two bounds, either optional. `min`/`max` and `from`/`to` are the same
 *  control with a different input type, so they are one component. */
const RangeFilter = ({ value, onChange, type, labels, ...inputProps }) => {
  const [lo, hi] = labels;
  const current = value || {};
  const set = (key) => (e) => onChange(orUndefined({ ...current, [key]: e.target.value }));

  return (
    <>
      <label className="tw-flex tw-flex-col tw-gap-1">
        <FieldLabel>{lo.label}</FieldLabel>
        <Input
          size="sm"
          type={type}
          value={current[lo.key] || ""}
          onChange={set(lo.key)}
          {...inputProps}
        />
      </label>
      <label className="tw-flex tw-flex-col tw-gap-1">
        <FieldLabel>{hi.label}</FieldLabel>
        <Input
          size="sm"
          type={type}
          value={current[hi.key] || ""}
          onChange={set(hi.key)}
          {...inputProps}
        />
      </label>
    </>
  );
};

const ColumnFilter = ({ column, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const { anchorRef, layerRef, style } = useAnchoredLayer({
    open,
    onClose: () => setOpen(false),
    width: PANEL_WIDTH,
  });

  const spec = typeof column.filter === "string" ? { type: column.filter } : column.filter;
  const active = isColumnFilterActive(value);
  const name = typeof column.label === "string" ? column.label : column.key;

  const body = () => {
    switch (spec.type) {
      case "set":
        return <SetFilter value={value} onChange={onChange} options={spec.options || []} />;
      case "number":
        return (
          <RangeFilter
            value={value}
            onChange={onChange}
            type="number"
            labels={[{ key: "min", label: "Min" }, { key: "max", label: "Max" }]}
          />
        );
      case "date":
        return (
          <RangeFilter
            value={value}
            onChange={onChange}
            type="date"
            labels={[{ key: "from", label: "From" }, { key: "to", label: "To" }]}
          />
        );
      default:
        return <TextFilter value={value} onChange={onChange} placeholder={spec.placeholder} />;
    }
  };

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        // ⚠️ The name says which COLUMN and whether it is on. Twelve identical
        // "Filter" buttons across a header row is the icon-strip problem again,
        // one row up.
        aria-label={`Filter by ${name}${active ? " (filtered)" : ""}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "tw-inline-flex tw-h-5 tw-w-5 tw-shrink-0 tw-items-center tw-justify-center",
          "tw-rounded-[4px] tw-border-0 tw-bg-transparent tw-p-0 tw-cursor-pointer",
          "tw-transition-colors hover:tw-bg-surface-hover tw-focus-ring-inset",
          // Off, it is a hint; on, it is the reason the table is short. The
          // filled funnel is the only thing on screen that says a column is
          // hiding rows, so it takes the brand colour and keeps it.
          active ? "tw-text-primary" : "tw-text-content-subtle hover:tw-text-content"
        )}
      >
        <Filter size={12} fill={active ? "currentColor" : "none"} aria-hidden="true" />
      </button>

      {open &&
        createPortal(
          <div
            ref={layerRef}
            style={{ ...style, width: PANEL_WIDTH }}
            role="dialog"
            aria-label={`Filter by ${name}`}
            className={cn(
              "tw-flex tw-flex-col tw-gap-2 tw-rounded-sm tw-border tw-border-solid",
              "tw-border-line tw-bg-surface-raised tw-p-3 tw-shadow-popover"
            )}
          >
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
              <FieldLabel>{name}</FieldLabel>
              {active && (
                <button
                  type="button"
                  onClick={() => onChange(undefined)}
                  className={cn(
                    "tw-inline-flex tw-items-center tw-gap-1 tw-border-0 tw-bg-transparent tw-p-0",
                    "tw-text-[0.6875rem] tw-text-content-muted tw-cursor-pointer",
                    "hover:tw-text-content tw-focus-ring"
                  )}
                >
                  <X size={11} aria-hidden="true" /> Clear
                </button>
              )}
            </div>

            {body()}

            <AppButton size="xs" variant="outline" onClick={() => setOpen(false)}>
              Done
            </AppButton>
          </div>,
          document.body
        )}
    </>
  );
};

export default ColumnFilter;
